/**
 * M128 — Gedeelde datum/tijd-helpers voor Ads-rapportweergave.
 *
 * `formatFetchedAt(iso, tz)` — B1-Nederlands: "16 aug 2026 · 13:51"
 * in de opgegeven tijdzone (default Europe/Amsterdam). Gebruikt in
 * beide `PrivacyVaultPage.GoogleAdsBox` én de "Volgende stap"-widget
 * op het dashboard, zodat dezelfde import overal dezelfde string
 * oplevert.
 *
 * `formatPeriodNL(startIso, endIso)` — B1-Nederlands periode-string:
 * "16 augustus t/m 14 september 2026" (jaartal één keer, aan het eind).
 */

const _SHORT_MONTHS_NL = [
  "jan", "feb", "mrt", "apr", "mei", "jun",
  "jul", "aug", "sep", "okt", "nov", "dec",
];
const _LONG_MONTHS_NL = [
  "januari", "februari", "maart", "april", "mei", "juni",
  "juli", "augustus", "september", "oktober", "november", "december",
];

function _partsInTz(iso, tz) {
  const dt = new Date(iso);
  if (isNaN(dt.getTime())) return null;
  // Format via toLocaleString met NL en gekozen tz, split de onderdelen.
  const fmt = new Intl.DateTimeFormat("nl-NL", {
    year: "numeric", month: "2-digit", day: "2-digit",
    hour: "2-digit", minute: "2-digit", hour12: false,
    timeZone: tz || "Europe/Amsterdam",
  });
  const parts = {};
  for (const p of fmt.formatToParts(dt)) parts[p.type] = p.value;
  return {
    day: Number(parts.day),
    month: Number(parts.month),
    year: Number(parts.year),
    hour: parts.hour,
    minute: parts.minute,
  };
}

export function formatFetchedAt(iso, tz) {
  if (!iso) return "—";
  const p = _partsInTz(iso, tz);
  if (!p) return iso;
  const mon = _SHORT_MONTHS_NL[p.month - 1] || String(p.month);
  return `${p.day} ${mon} ${p.year} · ${p.hour}:${p.minute}`;
}

function _parseIsoDateOnly(isoDate) {
  // "2026-09-14" → {y,m,d} (no timezone game — dates are calendar days)
  if (!isoDate) return null;
  const [y, m, d] = isoDate.split("-").map(Number);
  if (!y || !m || !d) return null;
  return { y, m, d };
}

export function formatPeriodNL(startIso, endIso) {
  const s = _parseIsoDateOnly(startIso);
  const e = _parseIsoDateOnly(endIso);
  if (!s || !e) return `${startIso || "—"} — ${endIso || "—"}`;
  const sm = _LONG_MONTHS_NL[s.m - 1];
  const em = _LONG_MONTHS_NL[e.m - 1];
  if (s.y !== e.y) {
    return `${s.d} ${sm} ${s.y} t/m ${e.d} ${em} ${e.y}`;
  }
  if (s.m !== e.m) {
    return `${s.d} ${sm} t/m ${e.d} ${em} ${e.y}`;
  }
  return `${s.d} t/m ${e.d} ${em} ${e.y}`;
}

// ---------------------------------------------------------------------------
// M128 G3-afwerking — B1-NL nummer/datum helpers voor de connector-kaarten.
// Waarden komen uit Google-provider velden; UI moet ze in NL-notatie tonen.
// ---------------------------------------------------------------------------

/** `2026-09-12` → `12 september 2026`. Bij ongeldige input: `—`. */
export function formatLongDateNL(isoDate) {
  const p = _parseIsoDateOnly(isoDate);
  if (!p) return "—";
  const mn = _LONG_MONTHS_NL[p.m - 1];
  return `${p.d} ${mn} ${p.y}`;
}

/** Geheel getal met NL-duizendtal: `16569` → `16.569`. `null/undefined` → `—`. */
export function formatIntNL(n) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return Math.round(num).toLocaleString("nl-NL");
}

/** Decimale waarde met komma: `22.6, 1` → `22,6`. */
export function formatDecimalNL(n, digits = 1) {
  if (n == null) return "—";
  const num = Number(n);
  if (!Number.isFinite(num)) return "—";
  return num.toLocaleString("nl-NL",
    { minimumFractionDigits: digits, maximumFractionDigits: digits });
}

/** GSC CTR: Google levert een fractie (`0.006`) → NL-percentage `0,6%`. */
export function formatCtrPctNL(fraction, digits = 1) {
  if (fraction == null) return "—";
  const num = Number(fraction);
  if (!Number.isFinite(num)) return "—";
  return `${formatDecimalNL(num * 100, digits)}%`;
}

/** GSC positie: `22.6` → `22,6`. Lager = beter. */
export function formatPositionNL(n, digits = 1) {
  return formatDecimalNL(n, digits);
}

/** B1-NL pluralisatie: `pluralizeNL(1, "klik", "klikken")` → `1 klik`. */
export function pluralizeNL(n, singular, plural) {
  const val = Number(n);
  if (!Number.isFinite(val)) return `— ${plural}`;
  const rounded = Math.round(val);
  return `${formatIntNL(rounded)} ${rounded === 1 ? singular : plural}`;
}
